import { Close as CloseIcon } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, List, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FileItem, SqoFile, downloadFileFromUrl } from '@/common';
import { downloadFileFromTeamMember } from '../api/downloadFileFromTeamMember';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromDws } from '../api/downloadFileFromDs';
import { checkIfErrorIsAccessAuthorizationError } from '@/core/utils/getMessageFromError';

type TeamFilesModalProps = {
  isOpen: boolean;
  setIsOpen: (show: boolean) => void;
  teamId: string;
  files: SqoFile[];
};

const TeamFilesModal = ({ isOpen, setIsOpen, teamId, files }: TeamFilesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromTeamMember,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const downloadFileFromDsMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const res = result.res;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      checkIfErrorIsAccessAuthorizationError(error)
        ? enqueueSnackbar(
            t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
            { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
          )
        : enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t('Files', { defaultValue: 'Files' })}</Typography>
        <CloseIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={() => setIsOpen(false)} />
      </DialogTitle>
      <DialogContent>
        {files.length > 0 ? (
          <List>
            {files.map((item) => (
              <FileItem
                key={item.id}
                name={item.title || ''}
                uploadedBy={item.uploadedBy}
                uploadedAt={item.uploadedAt}
                fileName={item.fileName}
                storeType={item.storeType}
                onDownload={() => {
                  if (item.storeType === 'DocumentSystem' && item.dwsDok) {
                    downloadFileFromDsMutation.mutate({
                      dok: item.dwsDok,
                      filename: item.title || item.dwsDok.originalfilename,
                    });
                  } else {
                    downloadFileMutation.mutate({ id: teamId, fileId: item.id });
                  }
                }}
              />
            ))}
          </List>
        ) : (
          <Typography variant="subtitle1">{t('No files', { defaultValue: 'No files' })}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeamFilesModal;
